import React, { createContext, useContext, useEffect } from 'react'

import type { AnalyticsConfig } from '@/utils/analytics'
import { initAnalytics, trackPageView, trackEvent } from '@/utils/analytics'
import type { UTMParams } from '@/utils/utm'
import { getUTMParams } from '@/utils/utm'

/**
 * Analytics Context 类型定义
 */
interface AnalyticsContextType {
  utmParams: UTMParams
  trackEvent: (
    eventName: string,
    params?: Record<string, string | number | boolean>,
  ) => void
  trackPageView: (pagePath?: string, pageTitle?: string) => void
  isInitialized: boolean
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined,
)

/**
 * Analytics Provider Props
 */
interface AnalyticsProviderProps {
  children: React.ReactNode
  config?: AnalyticsConfig
}

/**
 * Analytics Provider 组件
 */
export function AnalyticsProvider({
  children,
  config,
}: AnalyticsProviderProps) {
  const [utmParams, setUtmParams] = React.useState<UTMParams>({})
  const [isInitialized, setIsInitialized] = React.useState(false)

  const syncUTMParams = () => {
    const latestUTM = getUTMParams()

    setUtmParams((currentUTM) => {
      const keys = new Set([
        ...Object.keys(currentUTM),
        ...Object.keys(latestUTM),
      ]) as Set<keyof UTMParams>

      for (const key of keys) {
        if (currentUTM[key] !== latestUTM[key]) {
          return latestUTM
        }
      }

      return currentUTM
    })

    return latestUTM
  }

  useEffect(() => {
    // 获取 UTM 参数
    syncUTMParams()

    // 初始化 GA4
    if (config?.measurementId && config.enabled !== false) {
      initAnalytics(config)
      setIsInitialized(true)
      return
    }

    setIsInitialized(false)
  }, [config])

  const handleTrackEvent = (
    eventName: string,
    params?: Record<string, string | number | boolean>,
  ) => {
    trackEvent(eventName, params, syncUTMParams())
  }

  const handleTrackPageView = (pagePath?: string, pageTitle?: string) => {
    trackPageView(pagePath, pageTitle, syncUTMParams())
  }

  const value: AnalyticsContextType = {
    utmParams,
    trackEvent: handleTrackEvent,
    trackPageView: handleTrackPageView,
    isInitialized,
  }

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}

/**
 * 使用 Analytics Context 的 Hook
 */
export function useAnalytics(): AnalyticsContextType {
  const context = useContext(AnalyticsContext)
  if (context === undefined) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider')
  }
  return context
}

/**
 * 高阶组件：为页面组件自动添加页面浏览追踪
 */
export function withPageTracking<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  options?: { pagePath?: string; pageTitle?: string },
) {
  return function PageTrackedComponent(props: P) {
    const { trackPageView } = useAnalytics()

    useEffect(() => {
      trackPageView(options?.pagePath, options?.pageTitle)
    }, [trackPageView])

    return <WrappedComponent {...props} />
  }
}
